import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

/**
 * Remplace un <input type="date"> natif par un champ texte masqué jj/mm/aaaa.
 *
 * Chrome n'accepte pas les touches du pavé numérique dans les <input type="date"> natifs
 * (bug Chromium connu, absent de Firefox) : le widget interne de saisie par segments ne
 * reconnaît que les keyCodes des chiffres de la rangée du haut. Un simple <input type="text">
 * n'a pas ce widget et accepte donc le pavé numérique nativement, dans tous les navigateurs.
 *
 * Le contrat externe (value/defaultValue/onChange en ISO yyyy-mm-dd, ref.value en lecture,
 * required/min/max) reste identique à celui d'un <input type="date"> pour rester un
 * remplacement direct des usages existants.
 *
 * L'affichage est toujours piloté par un state interne (jamais directement par la prop `value`) :
 * une saisie partielle ("27/0") n'est pas une date ISO valide, donc si on dérivait l'affichage
 * de `value` en mode contrôlé, chaque caractère tapé repartirait vers le parent comme "",
 * reviendrait tel quel, et effacerait aussitôt ce qui vient d'être tapé. On ne resynchronise
 * l'affichage depuis `value` que lors d'un changement externe réel (chargement de données...),
 * pas quand `value` n'est que l'écho de notre propre émission.
 */

const isoToDisplay = (iso) => {
    if (!iso || typeof iso !== "string") return "";
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return "";
    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
};

const displayToIso = (display) => {
    const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return "";
    const [, day, month, year] = match;
    return `${year}-${month}-${day}`;
};

const isRealCalendarDate = (display, iso) => {
    const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return false;
    const dayNum = Number(match[1]);
    const monthNum = Number(match[2]);
    const yearNum = Number(match[3]);
    const dateObj = new Date(iso);
    return !Number.isNaN(dateObj.getTime())
        && dateObj.getUTCFullYear() === yearNum
        && dateObj.getUTCMonth() + 1 === monthNum
        && dateObj.getUTCDate() === dayNum;
};

const DateInput = forwardRef(function DateInput({
    name,
    id,
    value,
    defaultValue,
    onChange,
    required = false,
    min,
    max,
    disabled = false,
    className,
    placeholder = "jj/mm/aaaa",
}, forwardedRef) {
    const isControlled = value !== undefined;
    const [display, setDisplay] = useState(() => isoToDisplay(isControlled ? value : defaultValue));
    const inputRef = useRef();
    // Miroir synchrone de la valeur ISO courante : contrairement au state React (appliqué au
    // prochain rendu), une ref se met à jour immédiatement, pour rester lisible via ref.current.value
    // même depuis un onChange qui lit la ref dans la même passe (comme le ferait un <input> natif).
    const currentIsoRef = useRef(displayToIso(isoToDisplay(isControlled ? value : defaultValue)));
    // Dernière valeur ISO que ce composant a lui-même émise via onChange, pour distinguer un
    // changement externe légitime de `value` d'un simple écho de notre propre saisie.
    const lastEmittedIsoRef = useRef(currentIsoRef.current);

    useEffect(() => {
        if (isControlled && value !== lastEmittedIsoRef.current) {
            const normalized = value || "";
            setDisplay(isoToDisplay(normalized));
            currentIsoRef.current = normalized;
            lastEmittedIsoRef.current = normalized;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const computeValidity = (displayStr, isoStr) => {
        if (displayStr === "") return !required;
        if (!isRealCalendarDate(displayStr, isoStr)) return false;
        if (min && isoStr < min) return false;
        if (max && isoStr > max) return false;
        return true;
    };

    // Le champ réel est un <input type="text">, donc min/max n'ont aucun effet natif sur sa
    // validité : sans setCustomValidity, un check basé sur field.validity.valid (comme
    // checkRequiredFields à la soumission) ne verrait jamais une date hors bornes.
    useEffect(() => {
        if (!inputRef.current) return;
        const iso = displayToIso(display);
        const valid = computeValidity(display, iso);
        inputRef.current.setCustomValidity(valid ? "" : "Date invalide");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [display, min, max, required]);

    useImperativeHandle(forwardedRef, () => ({
        get value() {
            return currentIsoRef.current;
        },
        focus: () => inputRef.current?.focus(),
    }));

    const emitChange = (nextDisplay) => {
        const nextIso = displayToIso(nextDisplay);
        currentIsoRef.current = nextIso;
        lastEmittedIsoRef.current = nextIso;
        setDisplay(nextDisplay);
        if (onChange) {
            onChange({
                target: {
                    name,
                    value: nextIso,
                    type: "date",
                    required,
                    validity: { valid: computeValidity(nextDisplay, nextIso) },
                    parentElement: inputRef.current?.parentElement,
                },
                preventDefault: () => {},
                stopPropagation: () => {},
            });
        }
    };

    const handleRawChange = (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
        let formatted = digits;
        if (digits.length > 4) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
        } else if (digits.length > 2) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }
        emitChange(formatted);
    };

    // Datepicker natif caché, synchronisé avec le champ texte : ouvert via l'icône calendrier
    // (showPicker), pour retrouver un calendrier sans réintroduire le bug clavier numérique de
    // <input type="date"> saisi directement (cf. commentaire en tête de fichier).
    const pickerRef = useRef();

    const handlePickerChange = (e) => {
        emitChange(isoToDisplay(e.target.value));
    };

    const openPicker = () => {
        if (disabled) return;
        if (pickerRef.current?.showPicker) {
            pickerRef.current.showPicker();
        } else {
            pickerRef.current?.focus();
            pickerRef.current?.click();
        }
    };

    return (
        <span className="date-input-wrapper">
            <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                name={name}
                id={id}
                className={className}
                placeholder={placeholder}
                value={display}
                onChange={handleRawChange}
                required={required}
                disabled={disabled}
                maxLength={10}
            />
            <i className="fa fa-calendar date-input-picker-icon" onClick={openPicker}/>
            <input
                ref={pickerRef}
                type="date"
                tabIndex={-1}
                aria-hidden="true"
                className="date-input-native-picker"
                value={currentIsoRef.current}
                min={min}
                max={max}
                disabled={disabled}
                onChange={handlePickerChange}
            />
        </span>
    );
});

export default DateInput;
